import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchFinancialFlows } from 'views/finance/moduleServices/flow_requests';
import { formatDateLocal } from 'shared_items/formatConventions';
import DetailedTransactionList from './viewComponents/Detailed/DetailedTransactionList';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const defaultFlowStartDate = () => {
  const d = new Date();
  return formatDateLocal(new Date(d.getFullYear(), 0, 1));
};

const defaultFlowEndDate = () => formatDateLocal(new Date());

const ViewFlowDetailed = ({ 
  hierarchicalCategories = {},
  onFlowClick,
  flowsUpdateRef
}) => {
  // ##### STATES
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [flowSearchText, setFlowSearchText] = useState('');
  const [selectedCategL1, setSelectedCategL1] = useState('');
  const [selectedCategL2, setSelectedCategL2] = useState('');
  const [flowStartDate, setFlowStartDate] = useState(defaultFlowStartDate);
  const [flowEndDate, setFlowEndDate] = useState(defaultFlowEndDate);
  const [containerHeight, setContainerHeight] = useState(600);

  // ###### EFFECTS
  useEffect(() => {
    const calculateHeight = () => {
      // Viewport height minus all UI elements
      const viewportHeight = window.innerHeight;
      const navbarHeight = 64; // navbar L2 height
      const paddingBottom = 96; // pb-24 from FinanceMain
      const margins = 5; // minimal margins
      const headerHeight = 80; // header height
      const containerPadding = 16; // p-2 from FinanceMain
      
      const availableHeight = viewportHeight - navbarHeight - paddingBottom - margins - headerHeight - containerPadding;
      // Maximize space usage, very close to navbar bottom
      const finalHeight = Math.max(150, Math.min(availableHeight + 80, viewportHeight - 80));
      setContainerHeight(finalHeight);
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const handleFlowStartDateChange = useCallback((value) => {
    if (!value || !ISO_DATE.test(value)) return;
    setFlowStartDate(value);
    setFlowEndDate((end) => (value > end ? value : end));
  }, []);

  const handleFlowEndDateChange = useCallback((value) => {
    if (!value || !ISO_DATE.test(value)) return;
    setFlowEndDate(value);
    setFlowStartDate((start) => (value < start ? value : start));
  }, []);

  // ###### LOAD DATA (limit/offset + server date filter — same API as board list)
  const loadAllFlows = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let allFlows = [];
      let offset = 0;
      let hasMoreData = true;
      const limit = 500;

      const params = {
        start_date: flowStartDate,
        end_date: flowEndDate,
      };

      while (hasMoreData) {
        const data = await fetchFinancialFlows({
          ...params,
          limit,
          offset,
        });

        if (data && data.length > 0) {
          allFlows = [...allFlows, ...data];
          offset += limit;
          hasMoreData = data.length === limit;
        } else {
          hasMoreData = false;
        }
      }

      setFlows(allFlows);
    } catch (err) {
      console.error('Error loading financial flows:', err);
      setError('Error loading transactions');
    } finally {
      setLoading(false);
    }
  }, [flowStartDate, flowEndDate]);

  useEffect(() => {
    loadAllFlows();
  }, [loadAllFlows]);

  useEffect(() => {
    if (flowsUpdateRef) {
      flowsUpdateRef.current = loadAllFlows;
    }
  }, [flowsUpdateRef, loadAllFlows]);

  useEffect(() => {
    setSelectedCategL1('');
    setSelectedCategL2('');
  }, [selectedCategory]);

  useEffect(() => {
    setSelectedCategL2('');
  }, [selectedCategL1]);

  // Flows after header filters (L0 + In/Out) — used to populate L1/L2 dropdowns
  const flowsAfterTypeAndL0 = useMemo(
    () =>
      flows.filter((flow) => {
        if (selectedType && flow.flow_type !== selectedType) return false;
        if (selectedCategory && flow.categ_l0 !== selectedCategory) return false;
        return true;
      }),
    [flows, selectedType, selectedCategory]
  );

  const categoriesL1 = useMemo(() => {
    const unique = [...new Set(flowsAfterTypeAndL0.map((f) => f.categ_l1).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [flowsAfterTypeAndL0]);

  const categoriesL2 = useMemo(() => {
    const base = selectedCategL1.trim()
      ? flowsAfterTypeAndL0.filter((f) => f.categ_l1 === selectedCategL1)
      : flowsAfterTypeAndL0;
    const unique = [...new Set(base.map((f) => f.categ_l2).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [flowsAfterTypeAndL0, selectedCategL1]);

  // ##### FILTERED DATA
  const filteredFlows = useMemo(() => {
    const accountNeedle = selectedAccount.trim();
    const searchLower = flowSearchText.trim().toLowerCase();
    return flows.filter((flow) => {
      if (selectedType && flow.flow_type !== selectedType) {
        return false;
      }

      if (selectedCategory && flow.categ_l0 !== selectedCategory) {
        return false;
      }

      if (selectedCategL1 && flow.categ_l1 !== selectedCategL1) {
        return false;
      }

      if (selectedCategL2 && flow.categ_l2 !== selectedCategL2) {
        return false;
      }

      if (accountNeedle) {
        const acc = String(flow.flow_account ?? '').trim();
        if (acc !== accountNeedle) {
          return false;
        }
      }

      if (searchLower) {
        const desc = String(flow.desc ?? '').toLowerCase();
        if (!desc.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [
    flows,
    selectedType,
    selectedCategory,
    selectedAccount,
    flowSearchText,
    selectedCategL1,
    selectedCategL2,
  ]);

  // Extract unique L0 categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(flows.map(flow => flow.categ_l0).filter(Boolean))];
    return uniqueCategories.sort();
  }, [flows]);

  const accounts = useMemo(() => {
    const unique = [...new Set(flows.map((f) => String(f.flow_account ?? '').trim()).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [flows]);

  // ###### RETURN VIEW
  return (
    <div className="p-0 w-full h-full">
      {/* Virtualized cards with integrated filters */}
      <DetailedTransactionList 
        flows={filteredFlows}
        height={containerHeight}
        itemSize={80}
        loading={loading}
        error={error}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        flowSearchText={flowSearchText}
        onFlowSearchTextChange={setFlowSearchText}
        selectedCategL1={selectedCategL1}
        setSelectedCategL1={setSelectedCategL1}
        selectedCategL2={selectedCategL2}
        setSelectedCategL2={setSelectedCategL2}
        categories={categories}
        categoriesL1={categoriesL1}
        categoriesL2={categoriesL2}
        accounts={accounts}
        flowStartDate={flowStartDate}
        flowEndDate={flowEndDate}
        onFlowStartDateChange={handleFlowStartDateChange}
        onFlowEndDateChange={handleFlowEndDateChange}
        hierarchicalCategories={hierarchicalCategories}
        onFlowClick={onFlowClick}
        onFlowUpdate={loadAllFlows}
      />
    </div>
  );
};

export default ViewFlowDetailed;
